import { useEffect } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Button } from 'primereact/button';
import {
  FormEntityProps,
  FieldEntityProps,
  OddFieldOutPropsType,
  ObjStrKeyStrVal,
} from '@shared/types';
import {
  arrayChunk,
  getSmsCheckbox,
  prepareFields,
  getInitialValues,
  getOddFieldOutProps,
  handleSelectMenuSuperGlobalSortOrder,
  submitHandler,
} from '@shared/utils';
import {
  AccessibleSmsCheckbox,
  AccessibleOddFieldOutElement,
} from '@shared/fields';
import { LpeField } from './fields';
import getFieldValidationSchema from './fieldValidation';
import './formik.css';

const selectFieldClassNames = {
  mainField: 'formField selectOptions',
  error: 'errorHide',
};
const textFieldClassNames = {
  mainField: 'formField textAndEmailFields',
  error: 'errorHide',
};
const smsCheckBoxFieldClassNames = {
  container: 'smsCheckBoxContainer',
  mainField: 'formField smsCheckBox',
  error: 'errorMessage',
  textAndErrorContainer: 'smsTextAndErrorContainer',
  disclaimerTextClassName: 'smsDisclaimer',
};

const sortFieldsByWeight = (fields: FieldEntityProps[]) => {
  return fields.sort((a: FieldEntityProps, b: FieldEntityProps) => {
    const aWeight = a.weight ?? null;
    const bWeight = b.weight ?? null;

    if (aWeight !== null && bWeight !== null) {
      return aWeight - bWeight;
    }
    // Unweighted fields sort ahead of weighted ones so the tail is always the
    // highest-weighted fields. Returning 0 for mixed pairs left the comparator
    // inconsistent, letting an unweighted field displace a weighted one.
    // Array.prototype.sort is stable, so equal fields keep their original order.
    if (aWeight !== null) return 1;
    if (bWeight !== null) return -1;
    return 0;
  });
};

/**
 * Main Form component leveraging Formik Library.
 *
 * @param props form data from db
 * @constructor
 */
const FormikForm = ({ ...props }: FormEntityProps): JSX.Element => {
  // if we have no fields we have no form...
  if (props.fields) {
    const formHandler = `${props.handlerUrl}/${props.uuid}`;

    const { selectOptions, hiddenFields, dependentSelectOptions } =
      prepareFields(props.fields);

    const { FormSchema, requiredFields } = getFieldValidationSchema({
      fields: props.fields,
    });

    const smsFieldAndType = getSmsCheckbox(props.fields);
    const fieldsNoHiddenOrSms = props.fields.filter(
      (field) => field.parameterName !== 'sms_opt_in' && !field.isHidden,
    );
    let oddFieldOutProps = getOddFieldOutProps(
      fieldsNoHiddenOrSms,
      selectOptions,
      dependentSelectOptions,
      selectFieldClassNames,
      textFieldClassNames,
    );
    const lpeFieldsProps = {
      fields: fieldsNoHiddenOrSms,
      requiredFields,
      selectFieldClassNames,
      textFieldClassNames,
      smsCheckBoxFieldClassNames,
      dependentSelectOptions,
      selectOptions,
    };
    const chunkedFieldsArray = arrayChunk(
      sortFieldsByWeight(lpeFieldsProps.fields),
      2,
    );

    const formik = useFormik<ObjStrKeyStrVal>({
      initialValues: getInitialValues(
        props.fields,
        hiddenFields,
        selectOptions,
      ),
      validationSchema: FormSchema,
      onSubmit: async (values) =>
        submitHandler(values, hiddenFields, formHandler),
    });

    if (oddFieldOutProps) {
      const isOddFieldTouchedAndError =
        formik.errors &&
        formik.touched &&
        !!formik.errors[oddFieldOutProps.field.parameterName] &&
        !!formik.touched[oddFieldOutProps.field.parameterName];

      oddFieldOutProps = {
        ...oddFieldOutProps,
        isTouchedAndError: isOddFieldTouchedAndError,
      } as OddFieldOutPropsType;
    }

    useEffect(() => {
      return () => {
        formik.resetForm();
      };
    }, []);

    return (
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className={'formContainer'}>
          <div className="formTitle">Learn More About Our Program</div>
          {chunkedFieldsArray.map((items, idx) => (
            <div key={idx} className={'fieldRow'}>
              {items.map((field, idx) => {
                const isTouchedAndError =
                  formik.errors && formik.touched
                    ? !!formik.errors[field.parameterName] &&
                      !!formik.touched[field.parameterName]
                    : false;

                if (field.parameterName === 'phone') {
                  Object.assign(lpeFieldsProps, { formik });
                }

                handleSelectMenuSuperGlobalSortOrder(
                  lpeFieldsProps.selectOptions,
                );

                return (
                  <div key={idx} className={'fieldDiv'}>
                    <LpeField
                      {...{ ...lpeFieldsProps, field, isTouchedAndError }}
                    />
                  </div>
                );
              })}
            </div>
          ))}

          {oddFieldOutProps && (
            <div className={'fieldRow'}>
              <div className={'fieldDiv'}>
                <AccessibleOddFieldOutElement
                  oddFieldOutProps={oddFieldOutProps}
                  requiredFields={requiredFields}
                />
              </div>
            </div>
          )}

          {smsFieldAndType?.field && (
            <AccessibleSmsCheckbox
              field={smsFieldAndType.field}
              smsCheckBoxFieldClassNames={smsCheckBoxFieldClassNames}
              isTouchedAndError={
                formik.errors &&
                formik.touched &&
                !!formik.errors['sms_opt_in'] &&
                !!formik.touched['sms_opt_in']
              }
              showErrorMessages={true}
            />
          )}

          <Button
            label={!formik.isSubmitting ? "Let's Connect" : ''}
            loading={formik.isSubmitting}
            type={'submit'}
            className={'submitButton'}
            aria-label="Let's Connect"
          />
        </form>
      </FormikProvider>
    );
  }

  return <></>;
};

export default FormikForm;
